import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  addImportRows,
  cancelImport,
  commitImport,
  listRecentImports,
  removeImportRow,
  selectImportRows,
  stageImport,
  undoImport,
  updateImportRow,
} from "@/services/importsService";
import { useRefreshImportedData } from "@/hooks/useImportReview";

export const RECENT_IMPORTS_KEY = ["recentImports"];

export const useRecentImports = (enabled = true) =>
  useQuery({
    queryKey: RECENT_IMPORTS_KEY,
    queryFn: listRecentImports,
    enabled,
  });

export const useStageImport = () =>
  useMutation({
    mutationFn: async ({ source, chunks = [], fileName }) => {
      if (source !== "csv") return stageImport({ source });
      const [first, ...rest] = chunks;
      let batch = await stageImport({ source, rows: first, fileName });
      try {
        for (const rows of rest) batch = await addImportRows(batch.id, rows);
      } catch (err) {
        // The caller never receives the id of a half-built batch, so close it here.
        await cancelImport(batch.id).catch(() => {});
        throw err;
      }
      return batch;
    },
  });

export const useUpdateImportRow = () =>
  useMutation({
    mutationFn: ({ batchId, rowId, selected, edits }) =>
      updateImportRow(batchId, rowId, { selected, edits }),
  });

export const useSelectImportRows = () =>
  useMutation({
    mutationFn: ({ batchId, rowIds, selected }) =>
      selectImportRows(batchId, rowIds, selected),
  });

export const useRemoveImportRow = () =>
  useMutation({
    mutationFn: ({ batchId, rowId }) => removeImportRow(batchId, rowId),
  });

const useAfterImportChange = () => {
  const queryClient = useQueryClient();
  const refreshImportedData = useRefreshImportedData();
  return () => {
    refreshImportedData();
    queryClient.invalidateQueries({ queryKey: RECENT_IMPORTS_KEY });
    queryClient.invalidateQueries({ queryKey: ["agentWantAds"] });
    queryClient.invalidateQueries({ queryKey: ["onboardingProgress"] });
  };
};

export const useCommitImport = () => {
  const after = useAfterImportChange();
  return useMutation({
    mutationFn: (batchId) => commitImport(batchId),
    onSuccess: after,
  });
};

export const useUndoImport = () => {
  const after = useAfterImportChange();
  return useMutation({
    mutationFn: (batchId) => undoImport(batchId),
    onSuccess: after,
  });
};

export const useCancelImport = () =>
  useMutation({ mutationFn: (batchId) => cancelImport(batchId) });
